import {
    ComplexShelfOperation,
    DynamicSegments,
    ShelfType,
    Drawer,
    AdvancedShelf,
    AdvancedUpperShelf,
    AdvancedLowerShelf,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {TemplateVariable} from 'components/customer/Preview3D/usePreview3DData';
import {
    makeZeroIfUndefined,
    convertShelfPositionToPercentage,
    getShelfStyle,
    getSimpleShelfSetBack,
} from 'components/customer/Preview3D/helpers';
import {DEFAULT_UPPER_PAIR_DEPTH} from 'components/customer/Preview3D/values/useWallOvenDynamicValues';
import {RoomPlannerFormValues} from 'components/customer/RoomPlanner/types';
import {checkHasDrawers} from 'components/customer/Preview3DCommon/hardware/useShakerDoor';

type FormValues = {
    height: number;
    width: number;
    depth: number;
    shelvesCount: number;
    shelvesVerticalCount: number;
    shelvesHorizontalCount: number;
    shelfType: ShelfType;
    drawers: Drawer[];
    drawerTop: number;
    drawerBottom: number;
    drawerGap: number;
    shelvesHeightReference: ShelvesTypeReference;
    partitionWidth: number;
    ladderFrames: number;
    toeKickHeight: number;
    shelfSetBack: number;
    simpleShelfType: number;
    rangehoodOpeningHeight: number;
    totalOvenAndDrawerHeight: number;
};

type Insert = {
    insert: number;
    depth?: number;
    label?: string;
};

type InsertWithWidth = Insert & {
    width: number;
};

type ShelvesTypeReference = {
    upper_shelves: number;
    lower_shelves: number;
};

const getCarcaseThickness = (values: RoomPlannerFormValues): number =>
    values.cabinet_carc_thickness ?? 16.5;

export const getShelvesFormValues = (
    values: PreviewFormValues,
    shelfType: ShelfType
) => {
    return {
        height: makeZeroIfUndefined(values.cabinet_height),
        width: makeZeroIfUndefined(values.cabinet_width),
        depth: makeZeroIfUndefined(values.cabinet_depth),
        shelvesCount: Number(values.simple_shelves_amount),
        shelvesVerticalCount: Number(values.cabinet_vert_shelves),
        shelvesHorizontalCount: Number(values.cabinet_hori_shelves),
        shelfType: shelfType,
        drawers: values.drawers as unknown as Drawer[],
        drawerTop: makeZeroIfUndefined(values.cabinet_drawer_top),
        drawerBottom: makeZeroIfUndefined(values.cabinet_drawer_bottom),
        drawerGap: makeZeroIfUndefined(values.cabinet_drawer_gap),
        shelvesHeightReference: {
            upper_shelves:
                makeZeroIfUndefined(values.cabinet_height) -
                values.cabinet_partition_height,
            lower_shelves: values.cabinet_partition_height,
        },
        partitionWidth: values.cabinet_partition_width
            ? values.cabinet_partition_width < 1
                ? makeZeroIfUndefined(values.cabinet_width) *
                  values.cabinet_partition_width
                : makeZeroIfUndefined(values.cabinet_partition_width)
            : 0,
        ladderFrames: values.cabinet_ladder_frames
            ? makeZeroIfUndefined(values.cabinet_ladder_frames)
            : 0,
        toeKickHeight: values.cabinet_toekick
            ? makeZeroIfUndefined(values.cabinet_toekick)
            : 0,
        shelfSetBack: getSimpleShelfSetBack(values),
        simpleShelfType: Number(values.simple_shelf_type),
        rangehoodOpeningHeight: Number(values.rangehood_opening_height),
        totalOvenAndDrawerHeight:
            makeZeroIfUndefined(values.cabinet_total_drawer_height) +
            makeZeroIfUndefined(values.oven_opening_height) +
            makeZeroIfUndefined(values.microwave_opening_height),
    };
};

export const generateHorizontalShelves = (
    values: RoomPlannerFormValues,
    formValues: FormValues,
    variables: TemplateVariable
) => {
    const inserts: {
        insert: number;
        depth?: number;
        hasInsetDrawer?: boolean;
        label?: string;
    }[] = [];
    const advancedShelves = values.shelves;
    const shelvesCount = values.cabinet_simple_shelves
        ? formValues?.shelvesCount
        : advancedShelves?.length;
    const offset =
        formValues.drawers && formValues.drawers.length > 0
            ? formValues.drawers[0].drawer_face_height + formValues.drawerTop
            : 0;

    const carcaseThickness = getCarcaseThickness(values);
    const hasToeKick = variables.hasToeKick;
    const hasRangehoodVent = variables.hasRangehoodVent;
    const hasInsetDrawer = variables.hasInsetDrawer;

    const toeKickWithDrawers =
        hasToeKick && formValues.drawers && formValues.drawers.length > 0;
    let toeKickShelfStartPosition = 0;

    if (toeKickWithDrawers && checkHasDrawers(formValues.drawers)) {
        const totalFaceHeight = formValues.drawers.reduce(
            (acc, obj) => acc + parseFloat(obj?.drawer_face_height.toString()),
            0
        );

        // Mirror the correction in generateDrawers: if the summed face heights
        // exceed height - toeKickHeight the values are inflated and the
        // runner_specs height (first dimension, e.g. 278 in "278x500") should
        // be used instead.
        const correctedDrawers =
            totalFaceHeight > formValues.height - formValues.toeKickHeight
                ? formValues.drawers.map((drawer) => {
                      if (
                          !drawer?.drawer_runner_specs ||
                          !drawer?.drawer_face_height
                      )
                          return drawer;
                      const runnerMatch =
                          drawer.drawer_runner_specs.match(/^(\d+)x/);
                      if (!runnerMatch) return drawer;
                      const runnerHeight = parseFloat(runnerMatch[1]);
                      if (drawer.drawer_face_height === runnerHeight)
                          return drawer;
                      return {...drawer, drawer_face_height: runnerHeight};
                  })
                : formValues.drawers;

        toeKickShelfStartPosition =
            correctedDrawers.reduce(
                (acc, obj) =>
                    acc + parseFloat(obj?.drawer_face_height.toString()),
                0
            ) +
            formValues.drawerTop +
            formValues.drawerBottom +
            formValues.drawerGap * (formValues.drawers.length - 1) +
            carcaseThickness;
    }

    const rangehoodSetback =
        hasRangehoodVent || Number(formValues.rangehoodOpeningHeight) > 0
            ? formValues.rangehoodOpeningHeight
            : 0;
    const insetDrawerSetback = hasInsetDrawer ? carcaseThickness : 0;

    let height =
        formValues?.height -
        (hasToeKick ? formValues.toeKickHeight : offset) -
        rangehoodSetback -
        toeKickShelfStartPosition;

    let wallOvenTotalHeight = 0;

    if (variables?.isWallOvenProduct) {
        height = formValues?.height - formValues.totalOvenAndDrawerHeight;
        wallOvenTotalHeight = formValues.totalOvenAndDrawerHeight;
    } else if (variables?.hasUpperPairCabinet) {
        const microwaveOpeningHeight = Number(values.microwave_opening_height);
        height =
            values.cabinet_height - microwaveOpeningHeight - carcaseThickness;
        wallOvenTotalHeight = microwaveOpeningHeight;
    }

    const depth = values.cabinet_depth - carcaseThickness;
    const insertPositionYOffset =
        formValues.toeKickHeight +
        rangehoodSetback +
        toeKickShelfStartPosition -
        insetDrawerSetback +
        wallOvenTotalHeight;

    const insetDrawerOffset = hasInsetDrawer ? carcaseThickness : 0;

    let computedDepth = depth;
    if (
        variables?.isWallOvenProduct &&
        values.cabinet_depth >= DEFAULT_UPPER_PAIR_DEPTH
    ) {
        computedDepth = DEFAULT_UPPER_PAIR_DEPTH - carcaseThickness;
    } else if (variables?.hasUpperPairCabinet) {
        computedDepth =
            values.cabinet_depth -
            100 -
            values.cabinet_ext_thickness -
            carcaseThickness;
    }

    for (
        let index = 1 - (toeKickWithDrawers ? 1 : 0);
        index <= shelvesCount;
        index++
    ) {
        const isToeKickShelfWithDrawers = toeKickWithDrawers && index === 0;

        if (values.cabinet_simple_shelves || isToeKickShelfWithDrawers) {
            const style = getShelfStyle(formValues.simpleShelfType);

            inserts.push({
                insert:
                    (height / (shelvesCount + 1)) * index +
                    insertPositionYOffset,
                depth:
                    computedDepth -
                    (isToeKickShelfWithDrawers ? 0 : formValues.shelfSetBack) -
                    insetDrawerOffset,
                hasInsetDrawer,
                label:
                    index === 0
                        ? 'Horizontal Partition'
                        : `Shelf ${index} - ${style}`,
            });
        } else {
            const selectedShelf = advancedShelves[index - 1];
            const style = getShelfStyle(selectedShelf?.shelf_style);

            if (selectedShelf && Number(selectedShelf.shelf_type) !== 9)
                inserts.push({
                    insert:
                        height *
                            convertShelfPositionToPercentage(
                                selectedShelf.shelf_position,
                                height
                            ) +
                        insertPositionYOffset,
                    depth:
                        computedDepth -
                        selectedShelf.shelf_offset -
                        insetDrawerOffset,
                    hasInsetDrawer,
                    label:
                        index === 0
                            ? 'Horizontal Partition'
                            : `Shelf ${index} - ${style}`,
                });
        }
    }

    return inserts;
};

export const generateComplexHorizontalShelves = (
    values: RoomPlannerFormValues,
    formValues: FormValues
) => {
    const shelvesCount = formValues?.shelvesHorizontalCount;
    const shelvesCountVertical = formValues?.shelvesVerticalCount;
    const complexShelfOperation: ComplexShelfOperation[] = [];
    const exteriorThickness = values.cabinet_ext_thickness;

    for (let index = 1; index <= shelvesCount; index++) {
        if (formValues?.shelfType == 'COMPLEX') {
            const segments: DynamicSegments[] = [];
            const insert = (formValues?.height / (shelvesCount + 1)) * index;

            for (let index2 = 1; index2 <= shelvesCountVertical; index2++) {
                const calculation =
                    (formValues?.width / (shelvesCountVertical + 1)) * index2;

                segments.push({
                    first: {
                        StartCoor: {
                            x: calculation - exteriorThickness,
                        },
                        EndCoor: {
                            x: calculation - exteriorThickness,
                        },
                    },
                    second: {
                        EndCoor: {
                            x: calculation,
                        },
                        StartCoor: {
                            x: calculation - exteriorThickness,
                        },
                    },
                    third: {
                        EndCoor: {
                            x: calculation,
                        },
                        StartCoor: {
                            x: calculation,
                        },
                    },
                    fourth: {
                        StartCoor: {
                            x: calculation,
                        },
                    },
                });
            }

            complexShelfOperation.push({
                insert,
                openBaseCutSegments: segments,
            });
        }
    }

    return {
        shelves: complexShelfOperation,
        depth: formValues.depth - exteriorThickness,
        width: formValues.width - exteriorThickness,
    };
};

export const generateComplexVerticalShelves = (
    values: RoomPlannerFormValues,
    formValues: FormValues
) => {
    const shelvesCount = formValues?.shelvesVerticalCount;
    const shelvesCountHorizontal = formValues?.shelvesHorizontalCount;
    const complexShelfOperation: ComplexShelfOperation[] = [];
    const exteriorThickness = values.cabinet_ext_thickness;

    for (let index = 1; index <= shelvesCount; index++) {
        if (formValues?.shelfType == 'COMPLEX') {
            const segments: DynamicSegments[] = [];
            const insert = (formValues?.width / (shelvesCount + 1)) * index;

            for (let index2 = 1; index2 <= shelvesCountHorizontal; index2++) {
                const calculation =
                    (formValues?.height / (shelvesCountHorizontal + 1)) *
                    index2;

                segments.push({
                    first: {
                        EndCoor: {
                            x: calculation,
                        },
                    },
                    second: {
                        EndCoor: {
                            x: calculation,
                        },
                        StartCoor: {
                            x: calculation,
                        },
                    },
                    third: {
                        EndCoor: {
                            x: calculation - exteriorThickness,
                        },
                        StartCoor: {
                            x: calculation,
                        },
                    },
                    fourth: {
                        EndCoor: {
                            x: calculation - exteriorThickness,
                        },
                        StartCoor: {
                            x: calculation - exteriorThickness,
                        },
                    },
                });
            }

            complexShelfOperation.push({
                insert,
                openBaseCutSegments: segments,
            });
        }
    }

    return {
        shelves: complexShelfOperation,
        depth: formValues.depth - exteriorThickness,
        height: formValues.height - exteriorThickness,
    };
};

export const generateUpperAndLowerShelves = (
    values: RoomPlannerFormValues,
    formValues: FormValues
) => {
    const upperShelvesCount = values?.upper_shelves
        ? (values?.upper_shelves as unknown as Array<number>)?.length
        : 0;
    const lowerShelvesCount = values?.lower_shelves
        ? (values?.lower_shelves as unknown as Array<number>)?.length
        : 0;
    const carcaseThickness = getCarcaseThickness(values);

    const upperShelfInserts: Insert[] = [];
    const lowerShelfInserts: Insert[] = [];
    const upperShelves =
        values.upper_shelves as unknown as AdvancedUpperShelf[];
    const lowerShelves =
        values.lower_shelves as unknown as AdvancedLowerShelf[];
    const shelvesHeightReference = formValues.shelvesHeightReference;

    const upperShelfOffset = shelvesHeightReference.lower_shelves;
    const height = formValues?.height - upperShelfOffset;
    const depth = values.cabinet_depth - carcaseThickness;
    const partitionPosition = shelvesHeightReference.lower_shelves;

    for (let index = 1; index <= upperShelvesCount; index++) {
        const selectedShelf = upperShelves[index - 1];
        const style = getShelfStyle(selectedShelf?.upper_shelf_style);

        if (selectedShelf && Number(selectedShelf.upper_shelf_type) !== 9)
            upperShelfInserts.push({
                insert:
                    height *
                        convertShelfPositionToPercentage(
                            selectedShelf.upper_shelf_position,
                            height
                        ) +
                    upperShelfOffset,
                depth: depth - Math.max(0, selectedShelf.upper_shelf_offset),
                label: `Upper Shelf ${index} - ${style}`,
            });
    }

    const height2 = upperShelfOffset;

    for (let index = 1; index <= lowerShelvesCount; index++) {
        const selectedShelf = lowerShelves[index - 1];
        const style = getShelfStyle(selectedShelf?.lower_shelf_style);

        if (selectedShelf && Number(selectedShelf.lower_shelf_type) !== 9)
            lowerShelfInserts.push({
                insert:
                    height2 *
                    convertShelfPositionToPercentage(
                        selectedShelf.lower_shelf_position,
                        height2
                    ),
                depth: depth - Math.max(0, selectedShelf.lower_shelf_offset),
                label: `Lower Shelf ${index} - ${style}`,
            });
    }

    return {upperShelfInserts, lowerShelfInserts, partitionPosition};
};

export const generateUpperAndLowerLeftRightShelves = (
    values: RoomPlannerFormValues,
    formValues: FormValues
) => {
    const upperShelvesCount = values?.upper_shelves
        ? (values?.upper_shelves as unknown as Array<number>)?.length
        : 0;
    const lowerLeftShelvesCount = values?.lower_shelves
        ? (values?.lower_shelves as unknown as Array<number>)?.length
        : 0;
    const lowerRightShelvesCount = values?.shelves
        ? (values?.shelves as unknown as Array<number>)?.length
        : 0;

    const upperShelfInserts: Insert[] = [];
    const lowerLeftShelfInserts: InsertWithWidth[] = [];
    const lowerRightShelfInserts: InsertWithWidth[] = [];
    const carcaseThickness = getCarcaseThickness(values);

    const upperShelves =
        values.upper_shelves as unknown as AdvancedUpperShelf[];
    const lowerLeftShelves =
        values.lower_shelves as unknown as AdvancedLowerShelf[];
    const lowerRightShelves = values.shelves as unknown as AdvancedShelf[];

    const shelvesHeightReference = formValues.shelvesHeightReference;

    const upperShelfOffset = shelvesHeightReference.lower_shelves;
    const height = formValues?.height - upperShelfOffset;
    const depth = values.cabinet_depth - carcaseThickness;
    const partitionPosition = shelvesHeightReference.lower_shelves;
    const verticalPartitionPosition =
        formValues.partitionWidth + carcaseThickness;
    const verticalPartitionHeight = upperShelfOffset - carcaseThickness;

    for (let index = 1; index <= upperShelvesCount; index++) {
        const selectedShelf = upperShelves[index - 1];
        const style = getShelfStyle(selectedShelf?.upper_shelf_style);

        if (selectedShelf && Number(selectedShelf.upper_shelf_type) !== 9)
            upperShelfInserts.push({
                insert:
                    height *
                        convertShelfPositionToPercentage(
                            selectedShelf.upper_shelf_position,
                            height
                        ) +
                    upperShelfOffset,
                depth: depth - Math.max(0, selectedShelf.upper_shelf_offset),
                label: `Upper Shelf ${index} - ${style}`,
            });
    }

    const height2 = upperShelfOffset;

    for (let index = 1; index <= lowerLeftShelvesCount; index++) {
        const selectedShelf = lowerLeftShelves[index - 1];
        const style = getShelfStyle(selectedShelf?.lower_shelf_style);

        if (selectedShelf && Number(selectedShelf.lower_shelf_type) !== 9)
            lowerLeftShelfInserts.push({
                insert:
                    height2 *
                    convertShelfPositionToPercentage(
                        selectedShelf.lower_shelf_position,
                        height2
                    ),
                depth: depth - Math.max(0, selectedShelf.lower_shelf_offset),
                width: formValues.partitionWidth - carcaseThickness,
                label: `BL ${index} - ${style}`,
            });
    }

    for (let index = 1; index <= lowerRightShelvesCount; index++) {
        const selectedShelf = lowerRightShelves[index - 1];
        const style = getShelfStyle(selectedShelf?.shelf_style);

        if (selectedShelf && Number(selectedShelf.shelf_type) !== 9)
            lowerRightShelfInserts.push({
                insert:
                    height2 *
                    convertShelfPositionToPercentage(
                        selectedShelf.shelf_position,
                        height2
                    ),
                depth: depth - selectedShelf.shelf_offset,
                width:
                    formValues.width -
                    formValues.partitionWidth -
                    carcaseThickness,
                label: `BR ${index} - ${style}`,
            });
    }

    return {
        upperShelfInserts,
        lowerLeftShelfInserts,
        lowerRightShelfInserts,
        partitionPosition,
        verticalPartitionPosition,
        verticalPartitionHeight,
    };
};

type FaceFlags = {
    hasFrontFace: boolean;
    hasBackFace: boolean;
    hasLeftFace: boolean;
    hasRightFace: boolean;
};

const resolveFaceFlags = (values: RoomPlannerFormValues): FaceFlags => ({
    hasBackFace: Boolean(values.cabinet_edge_L1),
    hasLeftFace: Boolean(values.cabinet_edge_W1),
    hasRightFace: Boolean(values.cabinet_edge_W2),
    hasFrontFace: Boolean(values.cabinet_edge_L2),
});

const ifPresent = (present: boolean, value?: number): number =>
    present ? (value ?? 0) : 0;

const ifAbsent = (present: boolean, value?: number): number =>
    present ? 0 : (value ?? 0);

// Returns thickness × (number of absent faces) for a face pair.
const absentFaceThickness = (
    faceAPresent: boolean,
    faceBPresent: boolean,
    thickness?: number
): number =>
    ifAbsent(faceAPresent, thickness) + ifAbsent(faceBPresent, thickness);

const computeFrameInsertPositions = (count: number, width: number): number[] =>
    Array.from({length: count}, (_, i) => (width / (count + 1)) * (i + 1));

// Shared face/thickness intermediates for kicker and bulkhead frames.
const getFrameFaceMetrics = (
    values: RoomPlannerFormValues,
    formValues: FormValues
) => {
    const flags = resolveFaceFlags(values);
    const {hasFrontFace, hasBackFace, hasLeftFace, hasRightFace} = flags;

    const carcaseThickness = getCarcaseThickness(values);
    const exteriorThickness = values.cabinet_ext_thickness;
    const thicknessDiff = exteriorThickness - carcaseThickness;
    const cabinetWidth = Number(formValues.width);
    const cabinetDepth = Number(values.cabinet_depth);

    const leftExterior = ifPresent(hasLeftFace, exteriorThickness);
    const rightExterior = ifPresent(hasRightFace, exteriorThickness);
    const frontExterior = ifPresent(hasFrontFace, exteriorThickness);
    const backExterior = ifPresent(hasBackFace, exteriorThickness);

    return {
        ...flags,
        carcaseThickness,
        exteriorThickness,
        thicknessDiff,
        cabinetWidth,
        cabinetDepth,
        leftExterior,
        rightExterior,
        frontExterior,
        backExterior,
        missingSideExterior: absentFaceThickness(
            hasLeftFace,
            hasRightFace,
            exteriorThickness
        ),
        missingFrontBackCarcase: absentFaceThickness(
            hasFrontFace,
            hasBackFace,
            carcaseThickness
        ),
    };
};

// Exterior / interior panel sizes and inserts shared by kicker and bulkhead.
const getFramePanelDimensions = (
    values: RoomPlannerFormValues,
    formValues: FormValues
) => {
    const metrics = getFrameFaceMetrics(values, formValues);
    const {
        hasFrontFace,
        hasBackFace,
        hasLeftFace,
        hasRightFace,
        carcaseThickness,
        exteriorThickness,
        thicknessDiff,
        cabinetWidth,
        cabinetDepth,
        leftExterior,
        rightExterior,
        frontExterior,
        backExterior,
        missingSideExterior,
        missingFrontBackCarcase,
    } = metrics;

    const width = cabinetWidth - leftExterior - rightExterior;
    const depth =
        cabinetDepth - carcaseThickness - frontExterior - backExterior;
    const interiorDepth = depth - carcaseThickness;
    const exteriorWidth = cabinetWidth + carcaseThickness;
    const skipThicknessDiff =
        values.ext_material?.brand_name === 'Non Supply' ||
        (!hasLeftFace && !hasRightFace && !hasFrontFace && !hasBackFace);
    const noFace =
        !hasLeftFace && !hasRightFace && !hasFrontFace && !hasBackFace;

    return {
        ...metrics,
        width,
        depth,
        interiorDepth,
        exteriorWidth,
        exteriorDepth:
            cabinetDepth -
            carcaseThickness -
            thicknessDiff +
            exteriorThickness +
            missingFrontBackCarcase -
            ifAbsent(hasBackFace, exteriorThickness) +
            ifAbsent(hasBackFace, thicknessDiff) +
            ifAbsent(hasFrontFace, thicknessDiff),
        exteriorFrontWidth: exteriorWidth,
        exteriorFrontX: width + carcaseThickness + rightExterior,
        exteriorBackWidth: exteriorWidth,
        interiorFrontWidth: width,
        interiorBackWidth:
            cabinetWidth -
            carcaseThickness -
            (skipThicknessDiff ? 0 : thicknessDiff * 2) +
            (noFace ? carcaseThickness * 2 : missingSideExterior),
        exteriorBackX: width + carcaseThickness,
        interiorDepthSides: interiorDepth + carcaseThickness * 2,
        exteriorBackWidthOffset: leftExterior,
        exteriorSideWidthOffset: ifAbsent(hasFrontFace, exteriorThickness),
    };
};

export const generateKickerLadderFrames = (
    values: RoomPlannerFormValues,
    formValues: FormValues
) => {
    const {
        width,
        interiorDepth,
        exteriorDepth,
        exteriorFrontWidth,
        exteriorFrontX,
        exteriorBackWidth,
        interiorFrontWidth,
        interiorBackWidth,
        thicknessDiff,
        exteriorBackX,
        interiorDepthSides,
        exteriorBackWidthOffset,
        exteriorSideWidthOffset,
    } = getFramePanelDimensions(values, formValues);

    const inserts = computeFrameInsertPositions(
        formValues.ladderFrames,
        width
    ).map((insert) => ({insert, depth: interiorDepth}));

    return {
        inserts,
        exteriorDepth,
        exteriorFrontWidth,
        exteriorFrontX,
        exteriorBackWidth,
        interiorFrontWidth,
        interiorDepth,
        interiorBackWidth,
        thicknessDiff,
        exteriorBackX,
        interiorDepthSides,
        exteriorBackWidthOffset,
        exteriorSideWidthOffset,
    };
};

const OPEN_FRAME_DEPTH = 80;

export const generateBulkheadLadderFrames = (
    values: RoomPlannerFormValues,
    formValues: FormValues
) => {
    const panel = getFramePanelDimensions(values, formValues);
    const {
        hasFrontFace,
        hasBackFace,
        hasLeftFace,
        hasRightFace,
        carcaseThickness,
        exteriorThickness,
        cabinetWidth,
        cabinetDepth,
    } = panel;

    const hasFrontAndBackFaces = hasFrontFace && hasBackFace;
    const ladderFramesCount =
        formValues.ladderFrames || Number(values.cabinet_ladder_frames);
    const noFrontWithBackFaces = !hasFrontFace && hasBackFace;
    const noBackWithFrontFaces = hasFrontFace && !hasBackFace;
    const noFaces =
        !hasBackFace && !hasFrontFace && !hasLeftFace && !hasRightFace;

    // Bulkhead-specific interior frame / bottom-panel extents.
    const width = cabinetWidth - exteriorThickness - carcaseThickness;
    const depth = cabinetDepth - carcaseThickness;
    const totalThickness = exteriorThickness + carcaseThickness;
    const thicknessDiff = exteriorThickness - carcaseThickness;

    const frameDepth = hasFrontAndBackFaces
        ? depth - carcaseThickness - exteriorThickness - thicknessDiff
        : OPEN_FRAME_DEPTH;
    let framePositionY = noBackWithFrontFaces
        ? frameDepth + carcaseThickness + exteriorThickness + thicknessDiff
        : depth;
    if (noFaces) {
        framePositionY = OPEN_FRAME_DEPTH + exteriorThickness + thicknessDiff;
    }

    const inserts = computeFrameInsertPositions(ladderFramesCount, width).map(
        (insert) => ({
            insert: insert + carcaseThickness,
            depth: frameDepth,
            positionY:
                framePositionY -
                totalThickness -
                thicknessDiff +
                (noFrontWithBackFaces ? exteriorThickness : 0),
        })
    );

    const bottomDepthAddition = hasFrontAndBackFaces ? 0 : carcaseThickness;
    const closedFrameInset = hasFrontAndBackFaces ? OPEN_FRAME_DEPTH : 0;
    const interiorSideDepth =
        depth -
        thicknessDiff * 2 +
        (noFrontWithBackFaces ? exteriorThickness : 0);
    const interiorSideWidthOffset = noBackWithFrontFaces ? carcaseThickness : 0;

    return {
        inserts,
        hasFrontFace,
        hasBackFace,
        hasLeftFace,
        hasRightFace,
        leftSideDepth:
            (hasLeftFace
                ? interiorSideDepth
                : OPEN_FRAME_DEPTH +
                  carcaseThickness * (hasFrontFace && hasBackFace ? 2 : 1)) +
            interiorSideWidthOffset,
        rightSideDepth:
            (hasRightFace
                ? interiorSideDepth
                : OPEN_FRAME_DEPTH +
                  carcaseThickness * (hasFrontFace && hasBackFace ? 2 : 1)) +
            interiorSideWidthOffset,
        exteriorDepth:
            panel.exteriorDepth +
            carcaseThickness -
            (noBackWithFrontFaces ? thicknessDiff : 0),
        exteriorFrontWidth: panel.exteriorFrontWidth + carcaseThickness,
        exteriorFrontX: panel.exteriorFrontX + carcaseThickness,
        exteriorBackWidth: panel.exteriorBackWidth + carcaseThickness,
        exteriorBackX: panel.exteriorBackX + carcaseThickness,
        interiorFrontWidth: panel.interiorFrontWidth + carcaseThickness,
        interiorDepth: panel.interiorDepth,
        interiorBackWidth: panel.interiorBackWidth + carcaseThickness,
        interiorDepthSides: panel.interiorDepthSides,
        exteriorBackWidthOffset: panel.exteriorBackWidthOffset,
        exteriorSideWidthOffset: panel.exteriorSideWidthOffset,
        bottomPanelY:
            depth -
            closedFrameInset +
            bottomDepthAddition -
            (carcaseThickness + exteriorThickness) -
            thicknessDiff -
            (noFrontWithBackFaces ? 80 - thicknessDiff : 0),
        bottomPanelDepth:
            depth -
            OPEN_FRAME_DEPTH -
            closedFrameInset +
            bottomDepthAddition -
            (carcaseThickness + exteriorThickness) -
            thicknessDiff,
        depth,
        width,
        thicknessDiff,
        frontAndBackBottomWidth:
            cabinetWidth -
            carcaseThickness * 2 -
            thicknessDiff * 2 +
            (!hasLeftFace ? exteriorThickness : 0) +
            (!hasRightFace ? exteriorThickness : 0),
        frontAndBackBottomX: carcaseThickness,
        bottomRightPositionX:
            width -
            80 +
            carcaseThickness -
            thicknessDiff +
            (!hasLeftFace ? exteriorThickness : 0),
        backBottomPositionY:
            depth -
            carcaseThickness * 2 -
            thicknessDiff * 2 +
            (noFrontWithBackFaces ? exteriorThickness : 0),
    };
};
